import { useTranslation } from "react-i18next";
import type { GroupedSectionKey } from "../field-groups";
import { formatBooleanValue, formatFieldValue } from "./format-value";
import type { Application } from "../../types";

interface ReviewRow {
  key: string;
  label: string;
  answer: string;
}

/** One read-only line: the question, then the answer — Figma's `Q/R` pattern (US-E3-09). */
function AnswerRow({ label, answer }: { label: string; answer: string }) {
  return (
    <div className="flex flex-col gap-xxs">
      <p className="text-xs-regular text-(--text-tertiary)">{label}</p>
      <p className="whitespace-pre-wrap text-sm-regular text-(--text-primary)">
        {answer}
      </p>
    </div>
  );
}

/**
 * One section of the review (US-E3-09) — read-only, with a single « Modifier » link back to the
 * step it came from. Renders BOTH the round's grouped fields and its authored questions in
 * `display_order`, same content DossierSectionStep edits, just without a single input control.
 *
 * ⚠️ Skips any field/question with nothing to show (`formatFieldValue` returning `null`) rather
 * than printing a blank line — an unanswered required item is `ReviewMissingItems`'s job to
 * surface, not this card's; showing both would say it twice, once vaguely.
 *
 * Team's `members` is not a schema field (it is a real sub-resource — see `field-groups.ts`'s own
 * note on `TEAM_GROUPS`), so its composition is summarised here by hand, the one place this card
 * is not purely generic.
 */
export function ReviewSectionCard({
  application,
  sectionKey,
  onEdit,
}: {
  application: Application;
  sectionKey: GroupedSectionKey;
  onEdit: () => void;
}) {
  const { t } = useTranslation("acquire");

  const section = application.schema.applicant.sections.find(
    (item) => item.key === sectionKey
  );
  const authored = application.schema.form.sections.filter(
    (item) => item.applicant_step === sectionKey && item.questions.length > 0
  );
  const savedValues = (application as unknown as Record<string, unknown>)[
    sectionKey
  ] as Record<string, unknown> | undefined;

  const fieldRows = (section?.fields ?? [])
    .slice()
    .sort((a, b) => a.display_order - b.display_order)
    .map((field): ReviewRow | null => {
      const raw = savedValues?.[field.id];
      const answer =
        field.input_type === "CHECKBOX"
          ? formatBooleanValue(raw, {
              yes: t("dossier.review.yes"),
              no: t("dossier.review.no"),
            })
          : formatFieldValue(field, raw);
      return answer === null
        ? null
        : { key: field.id, label: field.label, answer };
    })
    .filter((row): row is ReviewRow => row !== null);

  const questionRows = authored
    .flatMap((group) =>
      group.questions.map((question): ReviewRow | null => {
        const saved = application.answers.find(
          (answer) => answer.question_id === question.id
        )?.value;
        const answer = formatFieldValue(question, saved);
        return answer === null
          ? null
          : { key: question.id, label: question.question_text, answer };
      })
    )
    .filter((row): row is ReviewRow => row !== null);

  // `mode === "MULTIPLE"` with zero members is a real, reachable state (toggled but no one added
  // yet) — an empty joined string is not a composition to show, so it falls back to `null` just
  // like an unanswered field, rather than rendering a row with nothing after its label.
  const composition =
    sectionKey === "team" &&
    application.team.mode === "MULTIPLE" &&
    application.team.members.length > 0
      ? application.team.members
          .map((member) => `${member.name} (${member.role})`)
          .join(", ")
      : null;

  const rows: ReviewRow[] = [
    ...(composition !== null
      ? [
          {
            key: "composition",
            label: t("dossier.review.team-composition"),
            answer: composition,
          },
        ]
      : []),
    ...fieldRows,
    ...questionRows,
  ];

  if (rows.length === 0) return null;

  return (
    <section className="flex flex-col gap-md border-t border-b border-(--border-default) bg-surface-card px-md py-lg -mx-md sm:-mx-xl sm:px-xl xl:py-xl">
      <div className="flex items-center justify-between gap-sm">
        <h2 className="text-md-semibold text-(--text-primary)">
          {section?.title ?? sectionKey}
        </h2>
        <button
          type="button"
          onClick={onEdit}
          className="shrink-0 text-sm-semibold text-(--text-link) underline outline-none focus-visible:ring-2 focus-visible:ring-focus-ring"
        >
          {t("dossier.review.edit")}
        </button>
      </div>
      <div className="flex flex-col gap-md">
        {rows.map((row) => (
          <AnswerRow key={row.key} label={row.label} answer={row.answer} />
        ))}
      </div>
    </section>
  );
}
